import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EVENTS } from "@/lib/eventConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RSVPForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    guests: 1,
    attending_days: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleDay = (key) => {
    setForm((f) => {
      const has = f.attending_days.includes(key);
      return {
        ...f,
        attending_days: has
          ? f.attending_days.filter((k) => k !== key)
          : [...f.attending_days, key],
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    if (form.attending_days.length === 0) {
      toast.error("Please select at least one day you'll attend.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim(),
        guests: Number(form.guests) || 1,
        attending_days: form.attending_days,
        message: form.message.trim() || null,
      });
      toast.success("Thank you — your blessing is received.");
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't save your RSVP. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="glass-card rounded-[2.5rem] p-12 sm:p-16 text-center"
        data-testid="rsvp-success"
      >
        <p className="label-eyebrow">With gratitude</p>
        <h3 className="font-serif-display text-4xl sm:text-5xl text-[#4A5346] mt-4">
          Your presence is a gift.
        </h3>
        <p className="text-[#757E70] mt-6 leading-relaxed max-w-md mx-auto">
          We&rsquo;ve received your RSVP and can&rsquo;t wait to share these days with you.
          A confirmation will follow as the celebration draws near.
        </p>
        <button
          type="button"
          className="mt-10 text-sm tracking-[0.22em] uppercase text-[#8FA092] hover:text-[#6F8175] transition-colors"
          onClick={() => {
            setSubmitted(false);
            setForm({
              name: "",
              email: "",
              guests: 1,
              attending_days: [],
              message: "",
            });
          }}
          data-testid="rsvp-submit-another"
        >
          Submit another response
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="glass-card rounded-[2.5rem] p-8 sm:p-12 md:p-14"
      data-testid="rsvp-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
        <div>
          <label className="label-eyebrow" htmlFor="rsvp-name">
            Your name
          </label>
          <input
            id="rsvp-name"
            className="line-input mt-3"
            placeholder="As we should announce you"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="rsvp-name-input"
            required
          />
        </div>
        <div>
          <label className="label-eyebrow" htmlFor="rsvp-email">
            Email
          </label>
          <input
            id="rsvp-email"
            type="email"
            className="line-input mt-3"
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="rsvp-email-input"
            required
          />
        </div>
        <div>
          <label className="label-eyebrow" htmlFor="rsvp-guests">
            Number of guests
          </label>
          <input
            id="rsvp-guests"
            type="number"
            min="1"
            max="20"
            className="line-input mt-3"
            value={form.guests}
            onChange={(e) =>
              setForm({ ...form, guests: e.target.value })
            }
            data-testid="rsvp-guests-input"
          />
        </div>
        <div>
          <p className="label-eyebrow">Attending</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {EVENTS.map((ev) => {
              const active = form.attending_days.includes(ev.key);
              return (
                <button
                  key={ev.key}
                  type="button"
                  onClick={() => toggleDay(ev.key)}
                  className={`text-xs tracking-[0.18em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                    active
                      ? "bg-[#8FA092] text-white border-[#8FA092]"
                      : "bg-transparent text-[#4A5346] border-[#8FA092]/40 hover:border-[#8FA092]"
                  }`}
                  data-testid={`rsvp-day-${ev.key}`}
                >
                  {ev.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow" htmlFor="rsvp-message">
            A note or blessing (optional)
          </label>
          <textarea
            id="rsvp-message"
            className="line-input mt-3"
            placeholder="A wish, a verse, a memory…"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="rsvp-message-input"
          />
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <button
          type="submit"
          className="btn-soft"
          disabled={submitting}
          data-testid="rsvp-submit-button"
        >
          {submitting ? "Sending…" : "Send RSVP"}
        </button>
      </div>
    </form>
  );
}
